import React, { useEffect, useState, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
  useNodesState,
  useEdgesState,
  Panel
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { Maximize2, Minimize2, X, Info, Tag, Database, FileText, Code, ZoomIn, ZoomOut, Maximize } from 'lucide-react';

const nodeStyles = {
  start: 'bg-green-100 border-green-500',
  end: 'bg-red-100 border-red-500',
  process: 'bg-blue-100 border-blue-500',
  intent: 'bg-purple-100 border-purple-500',
  tool: 'bg-yellow-100 border-yellow-500'
};

const nodeTypeColors = {
  intent: { bg: '#e9d5ff', border: '#9333ea', text: '#6b21a8' },    // purple
  tool: { bg: '#fef08a', border: '#ca8a04', text: '#854d0e' },      // yellow
  summary: { bg: '#bfdbfe', border: '#2563eb', text: '#1e40af' },   // blue
  advice: { bg: '#c7d2fe', border: '#4f46e5', text: '#3730a3' },    // indigo
  error: { bg: '#fed7aa', border: '#ea580c', text: '#9a3412' },     // orange
  hitl: { bg: '#fbcfe8', border: '#db2777', text: '#9d174d' },      // pink
  basic: { bg: '#99f6e4', border: '#0d9488', text: '#115e59' },     // teal
  process: { bg: '#e2e8f0', border: '#475569', text: '#1e293b' }    // slate
};

const edgeColors = {
  default: '#64748b',
  conditional: '#8b5cf6',
  error: '#ef4444',
  success: '#10b981',
  tool: '#f59e0b',
  end: '#3b82f6'
};

const getNodeType = (id, name, data) => {
  if (id?.includes('intent') || (data?.type === 'intent_node')) return 'intent';
  if (id?.includes('tool') || (data?.type === 'tool_node')) return 'tool';
  if (id?.includes('summary') || name?.includes('Summary')) return 'summary';
  if (id?.includes('advice') || name?.includes('Advice')) return 'advice';
  if (id?.includes('error') || name?.includes('Error')) return 'error';
  if (id?.includes('HITL') || name?.includes('Human')) return 'hitl';
  if (id?.includes('basic') || name?.includes('Basic')) return 'basic';
  return 'process';
};

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 180, height: 70 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  return {
    nodes: nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 90,
          y: nodeWithPosition.y - 35,
        }
      };
    }),
    edges
  };
};

const NodeDetailsModal = ({ node, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  if (!node) return null;

  const rawData = node.originalData || {};
  const nodeType = getNodeType(rawData.id, rawData.name, rawData.data);
  const colors = nodeTypeColors[nodeType] || nodeTypeColors.process;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className={`absolute inset-0 backdrop-blur-sm bg-slate-900/30 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      ></div>

      <div
        className={`bg-white/50 rounded-xl shadow-2xl w-full max-h-[85vh] overflow-hidden flex flex-col transition-all duration-300 transform ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        style={{
          boxShadow: `0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1), 0 0 0 1px ${colors.border}40`,
          width: '30vw',
        }}
      >
        <div
          className="px-6 py-4 flex justify-between items-center border-b"
          style={{
            background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg}90)`,
            borderColor: colors.border
          }}
        >
          <div className="flex items-center">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
              style={{ backgroundColor: colors.border + '30' }}
            >
              {nodeType === 'tool' && <Database className="w-4 h-4" style={{ color: colors.text }} />}
              {nodeType === 'intent' && <FileText className="w-4 h-4" style={{ color: colors.text }} />}
              {nodeType === 'process' && <Code className="w-4 h-4" style={{ color: colors.text }} />}
              {!['tool', 'intent', 'process'].includes(nodeType) && <Info className="w-4 h-4" style={{ color: colors.text }} />}
            </div>
            <h3 className="text-lg font-bold" style={{ color: colors.text }}>
              {rawData.name || 'Node Details'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
            style={{ color: colors.text }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-grow bg-white">
          <div className="grid grid-cols-1 gap-5">
            {/* Basic Info */}
            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-2">
                <div className="bg-gray-100 p-1.5 rounded-md">
                  <Code className="w-4 h-4 text-gray-600" />
                </div>
                <h4 className="text-sm font-semibold text-gray-700">Node ID</h4>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 font-mono text-sm">
                {rawData.id}
              </div>
            </div>

            {/* Node Type */}
            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-2">
                <div className="bg-gray-100 p-1.5 rounded-md">
                  <Info className="w-4 h-4 text-gray-600" />
                </div>
                <h4 className="text-sm font-semibold text-gray-700">Node Type</h4>
              </div>
              <div
                className="p-3 rounded-lg border flex items-center space-x-2"
                style={{
                  backgroundColor: colors.bg + '30',
                  borderColor: colors.border + '50',
                  color: colors.text
                }}
              >
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: colors.border }}
                ></div>
                <span className="font-medium">{rawData.data?.type || nodeType}</span>
              </div>
            </div>

            {/* Description (if available) */}
            {rawData.metadata?.description && (
              <div className="flex flex-col space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-100 p-1.5 rounded-md">
                    <FileText className="w-4 h-4 text-gray-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-700">Description</h4>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 whitespace-pre-wrap text-gray-700">
                  {rawData.metadata.description}
                </div>
              </div>
            )}

            {/* Tags (if available) */}
            {rawData.metadata?.tags && rawData.metadata.tags.length > 0 && (
              <div className="flex flex-col space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-100 p-1.5 rounded-md">
                    <Tag className="w-4 h-4 text-gray-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-700">Tags</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {rawData.metadata.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Data (if available) */}
            {rawData.data && Object.keys(rawData.data).length > 0 && (
              <div className="flex flex-col space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-100 p-1.5 rounded-md">
                    <Database className="w-4 h-4 text-gray-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-700">Data</h4>
                </div>
                <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 overflow-x-auto text-xs text-gray-700">
                  {JSON.stringify(rawData.data, null, 2)}
                </pre>
              </div>
            )}

            {/* Additional Metadata (if available) */}
            {rawData.metadata && Object.keys(rawData.metadata).filter(key => !['description', 'tags'].includes(key)).length > 0 && (
              <div className="flex flex-col space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-100 p-1.5 rounded-md">
                    <Database className="w-4 h-4 text-gray-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-700">Additional Metadata</h4>
                </div>
                <pre className="bg-gray-50 p-4 rounded-lg border border-gray-200 overflow-x-auto text-xs text-gray-700">
                  {JSON.stringify(
                    Object.fromEntries(
                      Object.entries(rawData.metadata).filter(([key]) => !['description', 'tags'].includes(key))
                    ),
                    null,
                    2
                  )}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg text-gray-700 transition-colors shadow-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main component
const WorkflowVisualizer = ({ graphData }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const zoomIn = () => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomIn();
    }
  };

  const zoomOut = () => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomOut();
    }
  };

  const fitView = () => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({ padding: 0.2 });
    }
  };

  // Get edge color based on data and conditional status
  const getEdgeColor = (edge) => {
    if (edge.data === 'error') return edgeColors.error;
    if (edge.data === '__end__') return edgeColors.end;
    if (edge.data === 'tool') return edgeColors.tool;
    if (edge.conditional) return edgeColors.conditional;
    return edgeColors.default;
  };

  // Handle node click to show details
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  // Close the node details modal
  const closeNodeDetails = () => {
    setSelectedNode(null);
  };

  useEffect(() => {
    if (!graphData) return;

    // Transform backend nodes to ReactFlow format
    const flowNodes = Object.entries(graphData.nodes).map(([id, nodeData]) => {
      const nodeType = getNodeType(nodeData.id, nodeData.name, nodeData.data);
      const colors = nodeTypeColors[nodeType];

      return {
        id: nodeData.id,
        data: {
          label: (
            <div className="font-medium">
              {/* Node Name */}
              <div className="text-sm font-bold">{nodeData.name}</div>
              <div className="text-xs font-semibold opacity-90 mt-1">
                {nodeType.charAt(0).toUpperCase() + nodeType.slice(1)}
              </div>

              {/* Node Data */}
              {nodeData.data && nodeData.data.type && (
                <div className="text-xs opacity-80 mt-0.5">
                  {nodeData.data.type}
                </div>
              )}
            </div>
          )
        },
        position: { x: 0, y: 0 }, // Will be calculated by layout algorithm
        style: {
          background: colors.bg,
          borderColor: colors.border,
          borderWidth: 2,
          color: colors.text,
          borderRadius: 8,
          padding: 10,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          cursor: 'pointer', // Add pointer cursor to indicate clickable
          transition: 'all 0.2s ease' // Smooth hover transition
        },
        // Add hover effect
        className: 'hover:shadow-lg hover:translate-y-[-2px]',
        // Store original data for the modal
        originalData: nodeData
      };
    });

    // Transform backend edges to ReactFlow format
    const flowEdges = graphData.edges.map((edge, index) => {
      const edgeColor = getEdgeColor(edge);

      return {
        id: `e${index}`,
        source: edge.source,
        target: edge.target,
        label: edge.data,
        animated: edge.conditional,
        labelStyle: {
          fontSize: '10px',
          fill: edge.data === 'error' ? '#b91c1c' : '#475569',
          fontWeight: 500
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColor,
        },
        style: {
          stroke: edgeColor,
          strokeWidth: edge.data === 'error' ? 2.5 : 2,
        },
      };
    });

    // Apply layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      flowNodes,
      flowEdges
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [graphData, setNodes, setEdges]);

  // Toggle fullscreen mode
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else if (selectedNode) {
          setSelectedNode(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isFullScreen, selectedNode]);

  if (!graphData) {
    return (
      <div className="flex justify-center items-center h-full bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-gray-500 text-center p-6">
          <p className="mb-2">No workflow data available yet.</p>
          <p className="text-sm">Send a message in the chat to see the agent's workflow.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Node Details Modal - Always render but conditionally show content */}
      <NodeDetailsModal
        node={selectedNode}
        onClose={closeNodeDetails}
      />

      {isFullScreen && (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={toggleFullScreen}
              className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              title="Exit Full Screen"
            >
              <Minimize2 className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            onInit={setReactFlowInstance}
          >
            <Controls className="bg-white shadow-md rounded-md border border-gray-200" />
            <MiniMap
              nodeStrokeWidth={3}
              zoomable
              pannable
              className="bg-white shadow-md rounded-md border border-gray-200"
              nodeColor={(node) => {
                const nodeType = getNodeType(node.id, node.data?.label?.props?.children[0]?.props?.children, null);
                return nodeTypeColors[nodeType].border;
              }}
            />
            <Panel position="top-left" className="bg-white rounded-md shadow-md border border-gray-200 p-1 flex space-x-1 m-1">
              <button
                onClick={zoomIn}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={zoomOut}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={fitView}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title="Fit View"
              >
                <Maximize className="w-4 h-4 text-gray-700" />
              </button>
            </Panel>
            <Background variant="dots" gap={12} size={1} color="#f1f5f9" />
          </ReactFlow>

          {/* Legend in fullscreen mode */}
          <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-md border border-gray-200 text-xs">
            <div className="font-medium mb-2 text-gray-700">Node Types:</div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
              {Object.entries(nodeTypeColors).map(([type, colors]) => (
                type !== 'process' && (
                  <div key={type} className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-sm mr-1.5"
                      style={{
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                        borderWidth: 1,
                        borderStyle: 'solid'
                      }}
                    ></div>
                    <span className="text-gray-600">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                  </div>
                )
              ))}
            </div>
            <div className="mt-2 text-gray-500 text-[10px] flex items-center">
              <Info className="w-3 h-3 mr-1" />
              Click on any node to view details
            </div>
          </div>
        </div>
      )}

      <div className={`w-full h-[500px] border border-gray-200 rounded-lg shadow-sm bg-white relative ${isFullScreen ? 'hidden' : ''}`}>
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={toggleFullScreen}
            className="bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
            title="Full Screen"
          >
            <Maximize2 className="w-5 h-5 text-gray-700" />
          </button>
        </div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
          onInit={setReactFlowInstance}

        >
          <Controls className="bg-white shadow-md rounded-md border border-gray-200" />
          <MiniMap
            nodeStrokeWidth={3}
            zoomable
            pannable
            className="bg-white shadow-md rounded-md border border-gray-200"
            nodeColor={(node) => {
              const nodeType = getNodeType(node.id, node.data?.label?.props?.children[0]?.props?.children, null);
              return nodeTypeColors[nodeType].border;
            }}
          />
          <Panel position="top-left" className="bg-white rounded-md shadow-md border border-gray-200 p-1 flex space-x-1 m-1">
            <button
              onClick={zoomIn}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4 text-gray-700" />
            </button>
            <button
              onClick={zoomOut}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4 text-gray-700" />
            </button>
            <button
              onClick={fitView}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title="Fit View"
            >
              <Maximize className="w-4 h-4 text-gray-700" />
            </button>
          </Panel>
          <Background variant="dots" gap={12} size={1} color="#f1f5f9" />
        </ReactFlow>

        {/* Legend */}
        <div className="absolute bottom-2 left-2 bg-white p-2.5 rounded-lg shadow-md border border-gray-200 text-xs">
          <div className="font-medium mb-1.5 text-gray-700">Node Types:</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {Object.entries(nodeTypeColors).map(([type, colors]) => (
              type !== 'process' && (
                <div key={type} className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-sm mr-1.5"
                    style={{
                      backgroundColor: colors.bg,
                      borderColor: colors.border,
                      borderWidth: 1,
                      borderStyle: 'solid'
                    }}
                  ></div>
                  <span className="text-gray-600">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                </div>
              )
            ))}
          </div>
          <div className="mt-2 text-gray-500 text-[10px] flex items-center">
            <Info className="w-3 h-3 mr-1" />
            Click on any node to view details
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkflowVisualizer;